import React, {useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Box,
  Divider,
  Dialog
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventIcon from '@mui/icons-material/Event';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CalendarDetailsModal from './CalendarDetailsModal';
import { useAppData } from '../../../../../context/AppDataContext';
import EventStatusUpdate from './EventStatusUpdate';
import EventArchive from './EventArchive';
import { Assignment, AssignmentTurnedIn } from '@mui/icons-material';

const typeIcons = {
  1: '🗓️', // Event
  2: '📝', // Task
  3: '⏰', // Reminder
};
const typeLabels = { 1: 'Meeting', 2: 'Task', 3: 'Reminder' };
const priorityDots = {1 :'🔴', 2 : '🟡', 3 : '🟢'};



const RenderEventItem = ({groupEventList}) => {
  const {setSelectedEvent} = useAppData();
  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };
  return (
    <>
      {groupEventList.map((event)=>
        <ListItem 
            key={event.id}
            divider               
          >
          <ListItemText sx={{ minWidth: 40,maxWidth:40 }} onClick={()=>handleEventClick(event)} type="button">
            <div>{priorityDots[event.priority]}<br/></div>
            <div>{typeIcons[event.calendarType]}</div>
          </ListItemText>
          <ListItemText
            primary={
              <>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography type="button" variant="subtitle1" fontWeight="bold" onClick={()=>handleEventClick(event)}  >{event.title}</Typography>
                <EventStatusUpdate event={event}  />
                
              </Box>
              </>
            }
            secondary={
              <div onClick={()=>handleEventClick(event)} type="button">
                {`${moment(event.startDate).format("LL")} ${moment(event.startTime, "HH:mm:ss").format("hh:mm A")} - ${moment(event.endDate).format("LL")} ${moment(event.endTime, "HH:mm:ss").format("hh:mm A")}  `}
              </div>
            }
          />
        </ListItem>
      )}  
      <CalendarDetailsModal />
    </>
  )
}
const EventListView = () => {

  const {eventList} = useAppData();

  // 1. Group and Sort data
  const groupedEvents = useMemo(() => {
    const today = moment().startOf('day');
    
    const categories = {
      past: [],
      today: [],
      upcoming: []
    };

    eventList?.forEach(event => {
      const eventDate = moment(event.startDate);
      if (eventDate.isBefore(today, 'day')) {
        categories.past.push(event);
      } else if (eventDate.isSame(today, 'day')) {
        categories.today.push(event);
      } else {
        categories.upcoming.push(event);
      }
    });

    // Sort each group by start time
    const sortByTime = (a, b) => 
      moment(`${a.startDate} ${a.startTime}`).diff(moment(`${b.startDate} ${b.startTime}`));

    return {
      past: categories.past.sort(sortByTime),
      today: categories.today.sort(sortByTime),
      upcoming: categories.upcoming.sort(sortByTime)
    };
  }, [eventList]);

  // Helper to render individual list items
  
  

  return (
    <Box sx={{ maxWidth: 800, margin: 'auto', p: 2 }}>
      
      {/* Card 1: Past Due */}
      <Accordion sx={{ mb: 1, border: '1px solid #dee2e6' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography color="error" fontWeight="bold">
            Past Due / History ({groupedEvents.past.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <List disablePadding>
            {groupedEvents.past.length > 0 ? (
              <RenderEventItem groupEventList={groupedEvents.past} />
            ) : (
              <Typography sx={{ p: 2, textAlign: 'center' }} color="text.secondary">No past events</Typography>
            )}
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Card 2: Today (Default Expanded) */}
      <Accordion defaultExpanded sx={{ mb: 1, border: '1px solid #0d6efd' }}>
        <AccordionSummary sx={{borderBottom: '1px solid #0d6efd' }} expandIcon={<ExpandMoreIcon />}>
          <Typography color="primary" fontWeight="bold">
            Today's Schedule ({moment().format('MMM Do, YYYY')}) <span className='float-end ms-5'>({groupedEvents.today.length})</span>
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <List disablePadding>
            {groupedEvents.today.length > 0 ? (
                <RenderEventItem groupEventList={groupedEvents.today} />
              
            ) : (
              <Typography sx={{ p: 2, textAlign: 'center' }} color="text.secondary">Nothing scheduled for today</Typography>
            )}
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Card 3: Upcoming */}
      <Accordion sx={{ mb: 1, border: '1px solid #dee2e6' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography color="success.main" fontWeight="bold">
            Upcoming Events ({groupedEvents.upcoming.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <List disablePadding>
            {groupedEvents.upcoming.length > 0 ? (
              <RenderEventItem groupEventList={groupedEvents.upcoming}  />
            ) : (
              <Typography sx={{ p: 2, textAlign: 'center' }} color="text.secondary">No upcoming events</Typography>
            )}
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default EventListView;